<script setup lang="ts">
import {
  $deleteTableColumnAtSelection,
  $deleteTableRowAtSelection,
  $insertTableColumnAtSelection,
  $insertTableRowAtSelection,
  $isTableCellNode,
  $isTableRowNode,
  registerTablePlugin,
  registerTableSelectionObserver,
  setScrollableTablesActive,
  TableCellHeaderStates,
  TableCellNode,
  TableNode,
} from "@lexical/table";
import { mergeRegister } from "@lexical/utils";
import { useEventListener, useResizeObserver } from "@vueuse/core";
import { $copyNode, $createParagraphNode, $getNearestNodeFromDOMNode, $isElementNode, type LexicalNode } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, type Ref, ref, watch } from "vue";

import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import {
  ArrowDownIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowSquareDownIcon,
  ArrowSquareLeftIcon,
  ArrowSquareRightIcon,
  ArrowSquareUpIcon,
  ArrowUpIcon,
  CopyIcon,
  DotsHorizontalIcon,
  DotsVerticalIcon,
  MediumHeaderIcon,
  MinusIcon,
  PlusIcon,
  StandardTextIcon,
  TrashIcon,
} from "~/icons";
import { DropdownMenuItemKind, Placement } from "~/shared/enums";
import type { DropdownMenuSection } from "~/shared/types";
import { when } from "~/utils/wait";

const ADD_BUTTON_OFF_AXIS_PX = 14;
const MENU_BUTTON_ON_AXIS_PX = 20;
const MENU_BUTTON_OFF_AXIS_PX = 14;
const EDGE_THRESHOLD_PX = 6;
const DEFAULT_COLUMN_WIDTH_PX = 144;
const MIN_COLUMN_WIDTH_PX = 48;

const editor = useLexicalComposer();

const rootElement = ref<HTMLElement | null>(null);
const positioningAnchor = ref<HTMLElement | null>(null);
const columnButton = ref<HTMLElement | null>(null);
const rowButton = ref<HTMLElement | null>(null);
const colMenuButton = ref<HTMLElement | null>(null);
const rowMenuButton = ref<HTMLElement | null>(null);
const colMenuWrapper = ref<HTMLElement | null>(null);
const rowMenuWrapper = ref<HTMLElement | null>(null);
const hoveredTableElement = ref<HTMLTableElement | null>(null);
const nearRightEdge = ref(false);
const nearBottomEdge = ref(false);

const hoveringFlags = {
  columnButton: ref(false),
  rowButton: ref(false),
  colMenuButton: ref(false),
  rowMenuButton: ref(false),
};
const controlRefs = [
  { ref: columnButton, hovering: hoveringFlags.columnButton },
  { ref: rowButton, hovering: hoveringFlags.rowButton },
  { ref: colMenuButton, hovering: hoveringFlags.colMenuButton },
  { ref: rowMenuButton, hovering: hoveringFlags.rowMenuButton },
  { ref: colMenuWrapper, hovering: hoveringFlags.colMenuButton },
  { ref: rowMenuWrapper, hovering: hoveringFlags.rowMenuButton },
];
const tableTopPx = ref(0);
const tableLeftPx = ref(0);
const tableWidthPx = ref(0);
const tableHeightPx = ref(0);
const isEditable = ref(editor.isEditable());

// Per-column/row menu state
const hoveredColIndex = ref<number | null>(null);
const hoveredRowIndex = ref<number | null>(null);
const colCellRects = ref<{ left: number; width: number }[]>([]);
const rowCellRects = ref<{ top: number; height: number }[]>([]);
const colMenuDropdown = ref<InstanceType<typeof DropdownMenu> | null>(null);
const rowMenuDropdown = ref<InstanceType<typeof DropdownMenu> | null>(null);
const colMenuOpen = ref(false);
const rowMenuOpen = ref(false);
const activeColIndex = ref<number | null>(null);
const activeRowIndex = ref<number | null>(null);

// Column resizing
const resizingColIndex = ref<number | null>(null);
const hoveredColBoundaryIndex = ref<number | null>(null);
const startXPx = ref(0);
const startWidthPx = ref(0);

const watchMenuDropdown = (
  dropdown: Ref<InstanceType<typeof DropdownMenu> | null>,
  menuOpen: Ref<boolean>,
  activeIndex: Ref<number | null>,
  hoveredIndex: Ref<number | null>
) => {
  watch(
    () => dropdown.value?.isOpenOrOpening,
    (open) => {
      // oxlint-disable-next-line no-param-reassign
      menuOpen.value = open ?? false;
      // oxlint-disable-next-line no-param-reassign
      activeIndex.value = open ? hoveredIndex.value : null;
    }
  );
};

watchMenuDropdown(colMenuDropdown, colMenuOpen, activeColIndex, hoveredColIndex);
watchMenuDropdown(rowMenuDropdown, rowMenuOpen, activeRowIndex, hoveredRowIndex);

const showColumnControl = computed(
  () => isEditable.value && !!hoveredTableElement.value && (nearRightEdge.value || hoveringFlags.columnButton.value)
);
const showRowControl = computed(
  () => isEditable.value && !!hoveredTableElement.value && (nearBottomEdge.value || hoveringFlags.rowButton.value)
);
const columnButtonStyle = computed(() => ({
  top: `${tableTopPx.value}px`,
  left: `${tableLeftPx.value + tableWidthPx.value - 1}px`,
  width: `${ADD_BUTTON_OFF_AXIS_PX}px`,
  height: `${tableHeightPx.value}px`,
}));
const rowButtonStyle = computed(() => ({
  top: `${tableTopPx.value + tableHeightPx.value - 1}px`,
  left: `${tableLeftPx.value}px`,
  width: `${tableWidthPx.value}px`,
  height: `${ADD_BUTTON_OFF_AXIS_PX}px`,
}));

const visibleColIndex = computed(() => (colMenuOpen.value ? activeColIndex.value : hoveredColIndex.value));
const visibleRowIndex = computed(() => (rowMenuOpen.value ? activeRowIndex.value : hoveredRowIndex.value));

const showColMenu = computed(
  () => isEditable.value && (visibleColIndex.value !== null || hoveringFlags.colMenuButton.value)
);
const showRowMenu = computed(
  () => isEditable.value && (visibleRowIndex.value !== null || hoveringFlags.rowMenuButton.value)
);

const colMenuStyle = computed(() => {
  if (visibleColIndex.value === null) {
    return {};
  }
  const cell = colCellRects.value[visibleColIndex.value];
  if (!cell) {
    return {};
  }
  return {
    top: `${tableTopPx.value - MENU_BUTTON_OFF_AXIS_PX / 2}px`,
    left: `${cell.left + cell.width / 2 - MENU_BUTTON_ON_AXIS_PX / 2}px`,
    width: `${MENU_BUTTON_ON_AXIS_PX}px`,
    height: `${MENU_BUTTON_OFF_AXIS_PX}px`,
  };
});

const rowMenuStyle = computed(() => {
  if (visibleRowIndex.value === null) {
    return {};
  }
  const cell = rowCellRects.value[visibleRowIndex.value];
  if (!cell) {
    return {};
  }
  return {
    top: `${cell.top + cell.height / 2 - MENU_BUTTON_ON_AXIS_PX / 2}px`,
    left: `${tableLeftPx.value - MENU_BUTTON_OFF_AXIS_PX / 2}px`,
    width: `${MENU_BUTTON_OFF_AXIS_PX}px`,
    height: `${MENU_BUTTON_ON_AXIS_PX}px`,
  };
});

const getCellElement = (tableElement: HTMLTableElement, rowIndex: number, colIndex: number): HTMLElement | null => {
  const row = tableElement.querySelectorAll("tr")[rowIndex];
  return (row?.querySelectorAll("th, td")[colIndex] as HTMLElement) ?? null;
};

const selectCellAndRun = (
  findCell: (table: HTMLTableElement) => Element | null,
  action: (table: HTMLTableElement) => void
) => {
  const tableElement = hoveredTableElement.value;
  if (!tableElement) {
    return;
  }
  editor.focus();
  editor.update(() => {
    const cellElement = findCell(tableElement);
    if (!(cellElement instanceof HTMLElement)) {
      return;
    }
    const cellNode = $getNearestNodeFromDOMNode(cellElement);
    if (!$isTableCellNode(cellNode)) {
      return;
    }
    cellNode.selectEnd();
    action(tableElement);
  });
};

const focusEditorAfterMenuClose = () => {
  when(
    () => !colMenuOpen.value && !rowMenuOpen.value,
    () => {
      requestAnimationFrame(() => {
        editor.focus();
      });
    },
    400
  );
};

const deleteAxis = (index: number, isRow: boolean) => {
  selectCellAndRun(
    (table) => getCellElement(table, isRow ? index : 0, isRow ? 0 : index),
    () => (isRow ? $deleteTableRowAtSelection() : $deleteTableColumnAtSelection())
  );
  (isRow ? hoveredRowIndex : hoveredColIndex).value = null;
  (isRow ? activeRowIndex : activeColIndex).value = null;
};

watch(hoveredTableElement, (tableElement, previousTableElement) => {
  previousTableElement?.parentElement?.removeAttribute("data-on");
  tableElement?.parentElement?.setAttribute("data-on", "true");
});

const clearControls = () => {
  if (resizingColIndex.value !== null) {
    return;
  }
  hoveredColBoundaryIndex.value = null;
  hoveredTableElement.value?.style.removeProperty("cursor");
  if (!colMenuOpen.value && !rowMenuOpen.value) {
    hoveredTableElement.value = null;
  }
  nearRightEdge.value = false;
  nearBottomEdge.value = false;
  controlRefs.forEach(({ hovering }) => {
    // oxlint-disable-next-line no-param-reassign
    hovering.value = false;
  });
  if (!colMenuOpen.value) {
    hoveredColIndex.value = null;
  }
  if (!rowMenuOpen.value) {
    hoveredRowIndex.value = null;
  }
};

const insertColumnAfterLast = () => {
  selectCellAndRun(
    (table) => table.querySelector("tr")?.querySelector("th:last-child, td:last-child") ?? null,
    () => $insertTableColumnAtSelection(true)
  );
  clearControls();
};

const insertRowAfterLast = () => {
  selectCellAndRun(
    (table) => table.querySelector("tr:last-child")?.querySelector("th, td") ?? null,
    (tableElement) => {
      $insertTableRowAtSelection(true);
      const tableNode = $getNearestNodeFromDOMNode(tableElement);
      if (tableNode instanceof TableNode) {
        const newLastRow = tableNode.getLastChild();
        if ($isTableRowNode(newLastRow)) {
          const newFirstCell = newLastRow.getFirstChild();
          if ($isTableCellNode(newFirstCell)) {
            newFirstCell.selectStart();
          }
        }
      }
    }
  );
  clearControls();
};

const $deepCloneNode = <T extends LexicalNode>(node: T): T => {
  const clone = $copyNode(node);
  if ($isElementNode(node) && $isElementNode(clone)) {
    node.getChildren().forEach((child) => {
      clone.append($deepCloneNode(child));
    });
  }
  return clone;
};

const withTableNode = (fn: (tableNode: TableNode) => void) => {
  const tableElement = hoveredTableElement.value;
  if (!tableElement) {
    return;
  }
  editor.update(() => {
    const tableNode = $getNearestNodeFromDOMNode(tableElement);
    if (tableNode instanceof TableNode) {
      fn(tableNode);
    }
  });
};

const markHeaderOrNormal = (_index: number, isRow: boolean) => {
  withTableNode((tableNode) => {
    const headerState = isRow ? TableCellHeaderStates.ROW : TableCellHeaderStates.COLUMN;
    const rows = tableNode.getChildren().filter($isTableRowNode);
    if (isRow) {
      const row = rows[0];
      if (!row) {
        return;
      }
      row
        .getChildren()
        .filter($isTableCellNode)
        .forEach((cell) => {
          cell.toggleHeaderStyle(headerState);
        });
    } else {
      rows.forEach((row) => {
        const cell = row.getChildren().filter($isTableCellNode)[0];
        if (cell) {
          cell.toggleHeaderStyle(headerState);
        }
      });
    }
  });
};

const moveAxis = (index: number, isRow: boolean, direction: -1 | 1) => {
  withTableNode((tableNode) => {
    const rows = tableNode.getChildren().filter($isTableRowNode);
    const targetIndex = index + direction;
    if (isRow) {
      if (targetIndex < 0 || targetIndex >= rows.length) {
        return;
      }
      const target = rows[targetIndex];
      if (direction === -1) {
        target.insertBefore(rows[index]);
      } else {
        target.insertAfter(rows[index]);
      }
      const movedFirstCell = rows[index]?.getFirstChild();
      if ($isTableCellNode(movedFirstCell)) {
        movedFirstCell.selectStart();
      }
    } else {
      rows.forEach((row) => {
        const cells = row.getChildren().filter($isTableCellNode);
        if (targetIndex < 0 || targetIndex >= cells.length) {
          return;
        }
        const target = cells[targetIndex];
        if (direction === -1) {
          target.insertBefore(cells[index]);
        } else {
          target.insertAfter(cells[index]);
        }
      });
      const movedCell = rows[0]?.getChildren().filter($isTableCellNode)[targetIndex];
      movedCell?.selectStart();
      const colWidths = tableNode.getColWidths();
      if (colWidths?.[targetIndex] !== undefined) {
        const widths = [...colWidths];
        [widths[index], widths[targetIndex]] = [widths[targetIndex], widths[index]];
        tableNode.setColWidths(widths);
      }
    }
    focusEditorAfterMenuClose();
  });
};

const insertAxis = (index: number, isRow: boolean, direction: -1 | 1) => {
  selectCellAndRun(
    (table) => getCellElement(table, isRow ? index : 0, isRow ? 0 : index),
    () => {
      if (isRow) {
        const newRow = $insertTableRowAtSelection(direction === 1);
        const firstCell = newRow?.getFirstChild();
        if ($isTableCellNode(firstCell)) {
          firstCell.selectStart();
        }
      } else {
        const newCell = $insertTableColumnAtSelection(direction === 1);
        if ($isTableCellNode(newCell)) {
          newCell.selectStart();
        }
      }
      focusEditorAfterMenuClose();
    }
  );
};

const replicateAxis = (index: number, isRow: boolean) => {
  withTableNode((tableNode) => {
    const rows = tableNode.getChildren().filter($isTableRowNode);
    if (isRow) {
      const row = rows[index];
      if (!row) {
        return;
      }
      const newRow = $deepCloneNode(row);
      row.insertAfter(newRow);
      const firstCell = newRow.getFirstChild();
      if ($isTableCellNode(firstCell)) {
        firstCell.selectStart();
      }
    } else {
      let firstNewCell: LexicalNode | null = null;
      rows.forEach((row) => {
        const cell = row.getChildren().filter($isTableCellNode)[index];
        if (cell) {
          const newCell = $deepCloneNode(cell);
          cell.insertAfter(newCell);
          firstNewCell ??= newCell;
        }
      });
      if (firstNewCell && $isTableCellNode(firstNewCell as LexicalNode)) {
        (firstNewCell as TableCellNode).selectStart();
      }
      const colWidths = tableNode.getColWidths();
      if (colWidths) {
        tableNode.setColWidths(colWidths.flatMap((width, i) => (i === index ? [width, width] : width)));
      }
    }
    focusEditorAfterMenuClose();
  });
};

const clearAxis = (index: number, isRow: boolean) => {
  withTableNode((tableNode) => {
    const rows = tableNode.getChildren().filter($isTableRowNode);
    const clearCell = (cell: TableCellNode) => {
      cell.getChildren().forEach((child) => child.remove());
      cell.append($createParagraphNode());
    };
    if (isRow) {
      const row = rows[index];
      if (!row) {
        return;
      }
      row.getChildren().filter($isTableCellNode).forEach(clearCell);
      const firstCell = row.getChildren().filter($isTableCellNode)[0];
      firstCell?.selectStart();
    } else {
      rows.forEach((row) => {
        const cell = row.getChildren().filter($isTableCellNode)[index];
        if (cell) {
          clearCell(cell);
        }
      });
      const firstCell = rows[0]?.getChildren().filter($isTableCellNode)[index];
      firstCell?.selectStart();
    }
    focusEditorAfterMenuClose();
  });
};

const isFirstAxisHeader = (index: number, isRow: boolean): boolean => {
  const tableElement = hoveredTableElement.value;
  if (!tableElement) {
    return false;
  }
  const cellElement = getCellElement(tableElement, isRow ? index : 0, isRow ? 0 : index);
  if (!(cellElement instanceof HTMLElement)) {
    return false;
  }
  let result = false;
  editor.read(() => {
    const cellNode = $getNearestNodeFromDOMNode(cellElement);
    if ($isTableCellNode(cellNode)) {
      result = cellNode.hasHeaderState(isRow ? TableCellHeaderStates.ROW : TableCellHeaderStates.COLUMN);
    }
  });
  return result;
};

const withIndex = (indexRef: Ref<number | null>, fn: (index: number) => void) => () => {
  if (indexRef.value !== null) {
    fn(indexRef.value);
  }
};

const makeMenuSections = (indexRef: Ref<number | null>, isRow: boolean): DropdownMenuSection[] => {
  const index = indexRef.value ?? 0;
  const axisCount = (isRow ? rowCellRects : colCellRects).value.length;
  const isFirst = index === 0;
  const isSecond = index === 1;
  const isLast = index === axisCount - 1;
  const firstIsHeader = isFirstAxisHeader(0, isRow);
  const isHeader = isFirst && firstIsHeader;
  const moveBefore = isRow ? "up" : "left";
  const moveAfter = isRow ? "down" : "right";
  const insertBefore = isRow ? "above" : "left";
  const insertAfter = isRow ? "below" : "right";
  const moveBeforeIcon = isRow ? ArrowUpIcon : ArrowLeftIcon;
  const moveAfterIcon = isRow ? ArrowDownIcon : ArrowRightIcon;
  const insertBeforeIcon = isRow ? ArrowSquareUpIcon : ArrowSquareLeftIcon;
  const insertAfterIcon = isRow ? ArrowSquareDownIcon : ArrowSquareRightIcon;

  return [
    {
      title: "",
      items: [
        {
          title: isHeader ? "Convert from header" : "Convert to header",
          kind: DropdownMenuItemKind.BUTTON,
          icon: isHeader ? StandardTextIcon : MediumHeaderIcon,
          hidden: !isFirst,
          onClick: withIndex(indexRef, (i) => markHeaderOrNormal(i, isRow)),
        },
        {
          title: `Move ${moveBefore}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: moveBeforeIcon,
          hidden: isFirst,
          disabled: isSecond && firstIsHeader,
          disabledReason: "Can't move before header",
          onClick: withIndex(indexRef, (i) => moveAxis(i, isRow, -1)),
        },
        {
          title: `Move ${moveAfter}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: moveAfterIcon,
          hidden: isLast,
          disabled: isFirst && firstIsHeader,
          disabledReason: "Can't move header from first position",
          onClick: withIndex(indexRef, (i) => moveAxis(i, isRow, 1)),
        },
      ],
    },
    {
      title: "",
      items: [
        {
          title: `Insert ${insertBefore}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: insertBeforeIcon,
          disabled: isFirst && firstIsHeader,
          disabledReason: "Can't insert before header",
          onClick: withIndex(indexRef, (i) => insertAxis(i, isRow, -1)),
        },
        {
          title: `Insert ${insertAfter}`,
          kind: DropdownMenuItemKind.BUTTON,
          icon: insertAfterIcon,
          onClick: withIndex(indexRef, (i) => insertAxis(i, isRow, 1)),
        },
        {
          title: "Replicate",
          kind: DropdownMenuItemKind.BUTTON,
          icon: CopyIcon,
          onClick: withIndex(indexRef, (i) => replicateAxis(i, isRow)),
        },
      ],
    },
    {
      title: "",
      items: [
        {
          title: "Clear",
          kind: DropdownMenuItemKind.BUTTON,
          icon: MinusIcon,
          onClick: withIndex(indexRef, (i) => clearAxis(i, isRow)),
        },
        {
          title: "Delete",
          kind: DropdownMenuItemKind.BUTTON,
          icon: TrashIcon,
          onClick: withIndex(indexRef, (i) => deleteAxis(i, isRow)),
        },
      ],
    },
  ];
};

const colMenuSections = computed(() => makeMenuSections(visibleColIndex, false));
const rowMenuSections = computed(() => makeMenuSections(visibleRowIndex, true));

let unregisterListeners: () => void;

const getColumns = (tableElement: HTMLTableElement) => Array.from(tableElement.querySelectorAll("col"));

const updateTableGeometry = (tableElement: HTMLTableElement) => {
  const anchorElement = positioningAnchor.value;
  if (!anchorElement) {
    clearControls();
    return;
  }

  const anchorRect = anchorElement.getBoundingClientRect();
  const tableRect = tableElement.getBoundingClientRect();
  if (tableRect.width === 0 || tableRect.height === 0) {
    clearControls();
    return;
  }

  const wrapperRect = tableElement.parentElement?.getBoundingClientRect() ?? tableRect;
  const visibleLeftPx = Math.max(tableRect.left, wrapperRect.left);
  const visibleRightPx = Math.min(tableRect.right, wrapperRect.right);

  hoveredTableElement.value = tableElement;
  tableTopPx.value = tableRect.top - anchorRect.top;
  tableLeftPx.value = visibleLeftPx - anchorRect.left;
  tableWidthPx.value = visibleRightPx - visibleLeftPx;
  tableHeightPx.value = tableRect.height;

  const firstRow = tableElement.querySelector("tr");
  if (firstRow) {
    colCellRects.value = Array.from(firstRow.querySelectorAll("th, td")).map((cell) => {
      const rect = cell.getBoundingClientRect();
      const cellLeftPx = Math.max(rect.left, wrapperRect.left);
      return { left: cellLeftPx - anchorRect.left, width: Math.min(rect.right, wrapperRect.right) - cellLeftPx };
    });
  }

  rowCellRects.value = Array.from(tableElement.querySelectorAll("tr")).map((row) => {
    const rect = row.getBoundingClientRect();
    return { top: rect.top - anchorRect.top, height: rect.height };
  });
};

const refreshTableGeometry = () => {
  if (
    hoveredTableElement.value &&
    (showColumnControl.value || showRowControl.value || showColMenu.value || showRowMenu.value)
  ) {
    updateTableGeometry(hoveredTableElement.value);
  }
};

useResizeObserver(rootElement, refreshTableGeometry);
useEventListener(document, "scroll", refreshTableGeometry, { capture: true });

const getResizedWidthPx = (event: MouseEvent) =>
  Math.max(MIN_COLUMN_WIDTH_PX, Math.round(startWidthPx.value + event.clientX - startXPx.value));

const clearColumnResize = () => {
  resizingColIndex.value = null;
  document.body.style.cursor = "";
};

const startColumnResize = (event: MouseEvent) => {
  const tableElement = hoveredTableElement.value;
  const cell = event.target instanceof HTMLElement ? event.target.closest("th, td") : null;
  const index = hoveredColBoundaryIndex.value;
  const column = tableElement && index !== null ? getColumns(tableElement)[index] : undefined;
  if (event.button !== 0 || !column || !cell || cell.parentElement !== tableElement?.rows[0]) {
    return;
  }
  event.preventDefault();
  resizingColIndex.value = index;
  startXPx.value = event.clientX;
  startWidthPx.value = column.getBoundingClientRect().width;
  document.body.style.cursor = "col-resize";
};

const onColumnResizeEnd = () => {
  const index = resizingColIndex.value;
  const tableElement = hoveredTableElement.value;
  if (index !== null && tableElement) {
    const columns = getColumns(tableElement);
    const widthPx = Math.round(columns[index]?.getBoundingClientRect().width ?? 0);
    if (widthPx && widthPx !== Math.round(startWidthPx.value)) {
      withTableNode((tableNode) => {
        const colWidths = tableNode.getColWidths() ?? [];
        tableNode.setColWidths(
          columns.map((column, i) =>
            i === index ? widthPx : (colWidths[i] ?? Math.round(column.getBoundingClientRect().width))
          )
        );
      });
      editor.focus();
    }
  }
  clearColumnResize();
};

const onColumnResizeMove = (event: MouseEvent) => {
  const tableElement = hoveredTableElement.value;
  if (resizingColIndex.value === null || !tableElement) {
    return;
  }
  if (event.buttons === 0) {
    onColumnResizeEnd();
    return;
  }
  const column = getColumns(tableElement)[resizingColIndex.value];
  if (column) {
    column.style.width = `${getResizedWidthPx(event)}px`;
  }
  updateTableGeometry(tableElement);
};

const resizeListenerTarget = computed(() => (resizingColIndex.value === null ? null : window));
useEventListener(resizeListenerTarget, "mousemove", onColumnResizeMove);
useEventListener(resizeListenerTarget, "mouseup", onColumnResizeEnd);

const onPointerMove = (event: PointerEvent) => {
  if (!isEditable.value || resizingColIndex.value !== null) {
    return;
  }
  const target = event.target instanceof HTMLElement ? event.target : null;
  const tableElement = target?.closest("table") ?? target?.querySelector(":scope > table");
  if (!(tableElement instanceof HTMLTableElement)) {
    clearControls();
    return;
  }

  updateTableGeometry(tableElement);

  const tableRect = tableElement.getBoundingClientRect();
  const wrapperRect = tableElement.parentElement?.getBoundingClientRect() ?? tableRect;
  nearRightEdge.value = Math.min(tableRect.right, wrapperRect.right) - event.clientX <= EDGE_THRESHOLD_PX;
  nearBottomEdge.value = tableRect.bottom - event.clientY <= EDGE_THRESHOLD_PX;
  const nearTopEdge = event.clientY - tableRect.top <= EDGE_THRESHOLD_PX;
  const nearLeftEdge = event.clientX - tableRect.left <= EDGE_THRESHOLD_PX;

  const cellElement = target?.closest("th, td");
  if (cellElement instanceof HTMLTableCellElement) {
    hoveredColIndex.value = nearTopEdge ? cellElement.cellIndex : null;
    const rowElement = cellElement.closest("tr");
    hoveredRowIndex.value = nearLeftEdge && rowElement ? rowElement.rowIndex : null;
    const boundaryIndex =
      rowElement?.rowIndex === 0
        ? getColumns(tableElement).findIndex(
            (column) => Math.abs(column.getBoundingClientRect().right - event.clientX) <= EDGE_THRESHOLD_PX
          )
        : -1;
    hoveredColBoundaryIndex.value = boundaryIndex === -1 ? null : boundaryIndex;
  } else {
    hoveredColIndex.value = null;
    hoveredRowIndex.value = null;
    hoveredColBoundaryIndex.value = null;
  }
  tableElement.style.cursor = hoveredColBoundaryIndex.value !== null ? "col-resize" : "";
};

const isRelatedToControl = (related: Node | null) =>
  controlRefs.some(({ ref: r }) => r.value?.contains(related)) || editor.getRootElement()?.contains(related);

const onPointerLeave = (event: PointerEvent) => {
  const related = event.relatedTarget as Node | null;
  const match = controlRefs.find(({ ref: r }) => r.value?.contains(related));
  if (match) {
    match.hovering.value = true;
    return;
  }
  clearControls();
};

const onControlPointerLeave = (event: PointerEvent, hovering: Ref<boolean>) => {
  // oxlint-disable-next-line no-param-reassign
  hovering.value = false;
  if (isRelatedToControl(event.relatedTarget as Node | null)) {
    return;
  }
  clearControls();
};

const onButtonMouseDown = (event: MouseEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

onMounted(() => {
  if (!editor.hasNodes([TableNode])) {
    return;
  }
  setScrollableTablesActive(editor, true);
  unregisterListeners = mergeRegister(
    registerTablePlugin(editor),
    registerTableSelectionObserver(editor),
    editor.registerUpdateListener(refreshTableGeometry),
    editor.registerNodeTransform(TableNode, (tableNode) => {
      if (!tableNode.getColWidths()) {
        tableNode.setColWidths(Array(tableNode.getColumnCount()).fill(DEFAULT_COLUMN_WIDTH_PX));
      }
    }),
    editor.registerEditableListener((editable) => {
      isEditable.value = editable;
      if (!editable) {
        clearControls();
      }
    }),
    editor.registerRootListener((newRoot, oldRoot) => {
      rootElement.value = newRoot;
      oldRoot?.removeEventListener("pointermove", onPointerMove);
      oldRoot?.removeEventListener("pointerleave", onPointerLeave);
      oldRoot?.removeEventListener("mousedown", startColumnResize);
      newRoot?.addEventListener("pointermove", onPointerMove);
      newRoot?.addEventListener("pointerleave", onPointerLeave);
      newRoot?.addEventListener("mousedown", startColumnResize);
    })
  );
});

onUnmounted(() => {
  unregisterListeners?.();
  clearColumnResize();
  clearControls();
});
</script>

<template>
  <slot />
  <div ref="positioningAnchor" class="pointer-events-none absolute top-0 left-0 size-0" aria-hidden="true" />
  <!-- Per-column menu button (overlapping top border) -->
  <div
    v-if="showColMenu"
    ref="colMenuWrapper"
    class="absolute flex items-center justify-center overflow-hidden"
    :style="colMenuStyle">
    <DropdownMenu ref="colMenuDropdown" class="size-full" :sections="colMenuSections" :width-pixels="224" :distance="0">
      <Tooltip text="Column options" class="size-full">
        <button
          ref="colMenuButton"
          type="button"
          class="flex size-full items-center justify-center rounded-sm border border-md bg-std text-lt hover:bg-lt"
          aria-label="Column options"
          @pointerleave="onControlPointerLeave($event, hoveringFlags.colMenuButton)"
          @mousedown="onButtonMouseDown">
          <DotsHorizontalIcon class="icon-xs" />
        </button>
      </Tooltip>
    </DropdownMenu>
  </div>
  <!-- Per-row menu button (overlapping left border) -->
  <div
    v-if="showRowMenu"
    ref="rowMenuWrapper"
    class="absolute flex items-center justify-center overflow-hidden"
    :style="rowMenuStyle">
    <DropdownMenu
      ref="rowMenuDropdown"
      class="size-full"
      :sections="rowMenuSections"
      :width-pixels="224"
      :placement="Placement.RIGHT_TOP"
      :distance="0">
      <Tooltip text="Row options" class="size-full">
        <button
          ref="rowMenuButton"
          type="button"
          class="flex size-full items-center justify-center rounded-sm border border-md bg-std text-lt hover:bg-lt"
          aria-label="Row options"
          @pointerleave="onControlPointerLeave($event, hoveringFlags.rowMenuButton)"
          @mousedown="onButtonMouseDown">
          <DotsVerticalIcon class="icon-xs" />
        </button>
      </Tooltip>
    </DropdownMenu>
  </div>
  <!-- Add column button (right edge) -->
  <Tooltip v-if="showColumnControl" text="Add column" no-wrapper>
    <template #default="{ triggerRef }">
      <button
        :ref="
          (e) => {
            columnButton = e as HTMLElement | null;
            triggerRef(e as HTMLElement | null);
          }
        "
        type="button"
        class="absolute flex items-center justify-center rounded-r border border-md bg-std text-lt hover:bg-lt"
        :style="columnButtonStyle"
        aria-label="Add table column"
        @pointerleave="onControlPointerLeave($event, hoveringFlags.columnButton)"
        @mousedown="onButtonMouseDown"
        @click="insertColumnAfterLast">
        <PlusIcon class="icon-xs" />
      </button>
    </template>
  </Tooltip>
  <!-- Add row button (bottom edge) -->
  <Tooltip v-if="showRowControl" text="Add row" no-wrapper>
    <template #default="{ triggerRef }">
      <button
        :ref="
          (e) => {
            rowButton = e as HTMLElement | null;
            triggerRef(e as HTMLElement | null);
          }
        "
        type="button"
        class="absolute flex items-center justify-center rounded-b border border-md bg-std text-lt hover:bg-lt"
        :style="rowButtonStyle"
        aria-label="Add row"
        @pointerleave="onControlPointerLeave($event, hoveringFlags.rowButton)"
        @mousedown="onButtonMouseDown"
        @click="insertRowAfterLast">
        <PlusIcon class="icon-xs" />
      </button>
    </template>
  </Tooltip>
</template>
